import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { TRAIT_TYPES, TraitCategory, TraitConfig, TraitRow, createConfig } from "../data";
import { AiPanel, Stepper } from "./ui";
import StepType from "./step-type";
import StepConfigure from "./step-configure";
import StepPreview from "./step-preview";
import StepSaved from "./step-saved";

interface BuilderState {
  category?: TraitCategory;
  trait?: TraitRow; // opened from the list's View Details / Edit Trait
  step?: number;
}

// Trait builder: Choose Type → Configure → Preview → Save and Use. Mounted
// under both the CRM (/traits/new) and Unify (/unify/traits/new) layouts.
// Nothing is persisted — Save just moves to the confirmation step.
const NewTrait: React.FC = () => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation() as { pathname: string; state: BuilderState | null };
  const listPath = pathname.startsWith("/unify") ? "/unify/traits/all" : "/traits";

  const start = state?.category ?? null;
  const [category, setCategory] = useState<TraitCategory | null>(start);
  const [config, setConfig] = useState<TraitConfig>(() => createConfig(start ?? "Custom", state?.trait));
  // Coming from the list skips straight to the step it asked for; picking a
  // type from a category card skips Choose Type.
  const [step, setStep] = useState(state?.step ?? (start ? 2 : 1));
  const [aiOpen, setAiOpen] = useState(true);

  const update = (patch: Partial<TraitConfig>) => setConfig((c) => ({ ...c, ...patch }));
  const pick = (c: TraitCategory) => {
    setCategory(c);
    setConfig(createConfig(c));
  };

  const buildTitle = category ? TRAIT_TYPES.find((t) => t.category === category)?.buildTitle : null;

  const reset = () => {
    setCategory(null);
    setConfig(createConfig("Custom"));
    setStep(1);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden flex flex-col min-h-[calc(100vh-7.5rem)]">
      <div className="flex items-center gap-4 px-6 pt-6 pb-5">
        <button
          type="button"
          onClick={() => navigate(listPath)}
          title="Back to traits"
          className="w-10 h-10 rounded-full border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex items-center justify-center text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl text-gray-900 dark:text-gray-100 flex items-center gap-2.5 flex-wrap">
          <button type="button" onClick={() => navigate(listPath)} className="text-gray-400 hover:text-gray-600">
            Back to traits
          </button>
          <span className="mx-3">/</span>
          {buildTitle ? `New ${buildTitle}` : "New Trait"}
          <span
            title="Creating traits isn't backed by a real API yet — nothing here is saved on the server."
            className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded"
          >
            Demo
          </span>
        </h1>
      </div>

      <Stepper current={step} />

      <div className={`flex-1 grid grid-cols-1 min-h-0 ${aiOpen ? "lg:grid-cols-[minmax(0,1fr)_380px]" : ""}`}>
        <div className="bg-slate-50 dark:bg-gray-950 px-6 sm:px-8 py-8 overflow-y-auto">
          {step === 1 && <StepType category={category} onSelect={pick} onNext={() => setStep(2)} />}
          {step === 2 && <StepConfigure config={config} update={update} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
          {step === 3 && (
            <StepPreview
              config={config}
              onBack={() => setStep(2)}
              onSave={() => {
                setStep(4);
                toast.success("Trait saved (demo only — the traits API doesn't support creating traits yet)");
              }}
            />
          )}
          {step === 4 && <StepSaved config={config} onEdit={() => setStep(2)} onCreateNew={reset} onBackToTraits={() => navigate(listPath)} />}
        </div>
        {aiOpen ? (
          <AiPanel onClose={() => setAiOpen(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setAiOpen(true)}
            title="Open ConvoAI"
            className="fixed bottom-6 right-6 w-12 h-12 rounded-xl bg-emerald-300 hover:bg-emerald-400 text-gray-900 shadow-md flex items-center justify-center"
          >
            <span className="text-xl leading-none">…</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default NewTrait;
